import { useEffect, useRef, useMemo } from 'react';
import Lenis from 'lenis';

/**
 * Custom hook para implementar smooth scrolling con Lenis
 * @param {Object} options - Opciones de configuración para Lenis
 * @returns {Object} - Instancia de Lenis y métodos de control
 */
export const useLenis = (options = {}) => {
    const lenisRef = useRef(null);
    
    // Memoizar las opciones para evitar recreación en cada render
    const memoizedOptions = useMemo(() => options, [JSON.stringify(options)]);

    useEffect(() => {
        // Configuración por defecto de Lenis optimizada para blog
        const defaultOptions = {
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
            direction: 'vertical',
            gestureDirection: 'vertical',
            smooth: true,
            mouseMultiplier: 1,
            smoothTouch: false, // Mantener scroll nativo en móviles para mejor UX
            touchMultiplier: 2,
            infinite: false,
            autoResize: true,
            wrapper: window,
            content: document.documentElement,
            wheelMultiplier: 1,
            normalizeWheel: true,
            ...memoizedOptions
        };

        // Crear instancia de Lenis
        const lenis = new Lenis(defaultOptions);
        lenisRef.current = lenis;

        // Agregar clase al HTML para estilos CSS
        document.documentElement.classList.add('lenis');

        // Función de animación optimizada
        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        // Iniciar el loop de animación
        const rafId = requestAnimationFrame(raf);

        // Eventos opcionales para debugging (solo en desarrollo)
        if (process.env.NODE_ENV === 'development') {
            lenis.on('scroll', ({ scroll, limit, velocity, direction, progress }) => {
                console.log({ scroll, limit, velocity, direction, progress });
            });
        }

        // Cleanup function
        return () => {
            document.documentElement.classList.remove('lenis');
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, [memoizedOptions]);

    // Retornar métodos útiles para controlar Lenis
    return {
        lenis: lenisRef.current,
        scrollTo: (target, options) => lenisRef.current?.scrollTo(target, options),
        start: () => lenisRef.current?.start(),
        stop: () => lenisRef.current?.stop(),
    };
};

export default useLenis;
